import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import PhishingIcon from '@mui/icons-material/Phishing';
import SetMealIcon from '@mui/icons-material/SetMeal';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import User from '../../Models/UserModels/User';
import WeightNote from '../../Models/WeightNoteModels/WeightNote';
import Gear from '../../Models/WeightNoteModels/Gear';
import Destiny from '../../Models/CatchRegistrationModels/Destiny';
import WeightNoteType from '../../Models/WeightNoteModels/WeightNoteType';
import FishingArea from '../../Models/CatchRegistrationModels/FishingArea';
import StorageMethod from '../../Models/CatchRegistrationModels/StorageMethod';
import FishingStock from '../../Models/CatchRegistrationModels/FishingStock';
import Condition from '../../Models/CatchRegistrationModels/Condition';
import CatchCombination from '../../Models/CatchRegistrationModels/CatchCombination';
import Landing from '../../Models/LandingModels/Landing';
import SnackBarComponent from '../../SharedComponents/Snackbar/SnackBarComponent';
import LandingStatuses from '../../Constants/LandingStatuses';
import WeightNoteTypes from '../../Constants/WeightNoteTypes';
import {
  getLandings,
  getWeightNotes,
  postBulkCorrection,
} from '../../Services/LondunarkerfiAPIService';
import dayjs from 'dayjs';
import FiltersDialog from '../Reports/components/Filters/FiltersDialog';
import { FilterPayload } from '../Reports/components/Filters/FilterDefinition';
import CatchRegistration from '../../Models/CatchRegistrationModels/CatchRegistration';
import API_PAGINATION_SIZE from '../../Constants/PaginationSize';

interface AdminCorrectionsProps {
  user: User;
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
  gears?: Gear[];
  destinies?: Destiny[];
  weightNoteTypes?: WeightNoteType[];
  fishingAreas?: FishingArea[];
  storageMethods?: StorageMethod[];
  fishingStocks?: FishingStock[];
  conditions?: Condition[];
  catchCombinations?: CatchCombination[];
  embedded?: boolean;
  onClose?: () => void;
  refetchLandings: () => Promise<void>;
}

type FieldType = 'select' | 'number' | 'text' | 'boolean';

interface FieldOption {
  value: string;
  label: string;
  type: FieldType;
  options?: Array<{ id: number; name: string }>;
}

const ADMIN_ROLE_ID = 18;
const MAX_LANDING_PAGES_TO_SCAN = 20;

const fmtDateTime = (value?: Date) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '');

const AdminCorrections: FC<AdminCorrectionsProps> = (props) => {
  const theme = useTheme();
  const [landingLoading, setLandingLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [landings, setLandings] = useState<Landing[]>([]);
  const [selectedLandingIds, setSelectedLandingIds] = useState<number[]>([]);
  const [weightNotes, setWeightNotes] = useState<WeightNote[]>([]);
  const [search, setSearch] = useState('');
  const [filterModel, setFilterModel] = useState<FilterPayload>({ logic: 'and', clauses: [] });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detailSearch, setDetailSearch] = useState('');
  const [expanded, setExpanded] = useState<number[]>([]);

  const gears = useMemo(() => props.gears ?? [], [props.gears]);
  const destinies = useMemo(() => props.destinies ?? [], [props.destinies]);
  const weightNoteTypes = useMemo(() => props.weightNoteTypes ?? [], [props.weightNoteTypes]);
  const fishingAreas = useMemo(() => props.fishingAreas ?? [], [props.fishingAreas]);
  const storageMethods = useMemo(() => props.storageMethods ?? [], [props.storageMethods]);
  const fishingStocks = useMemo(() => props.fishingStocks ?? [], [props.fishingStocks]);
  const conditions = useMemo(() => props.conditions ?? [], [props.conditions]);
  const catchCombinations = useMemo(() => props.catchCombinations ?? [], [props.catchCombinations]);

  const [selectedWeightNoteIds, setSelectedWeightNoteIds] = useState<number[]>([]);
  const [selectedWeightIds, setSelectedWeightIds] = useState<number[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [editTarget, setEditTarget] = useState<'weights' | 'weightNotes' | null>(null);

  const [field, setField] = useState<string>('gearId');
  const [toValue, setToValue] = useState<string | number | boolean | ''>('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bulkComment, setBulkComment] = useState('');
  const [bulkSubmitError, setBulkSubmitError] = useState('');
  const [bulkRefreshFailed, setBulkRefreshFailed] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const showSnackbar = (message: string, severity: string) => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity === 'error' ? 'error' : 'success');
    setSnackbarOpen(true);
  };

  const OP_ISL: Record<string, string> = {
    contains: 'Inniheldur',
    equals: 'Jafnt og',
    startsWith: 'Byrjar á',
    endsWith: 'Endar á',
    not: 'Er ekki',
    in: 'Er einhver af',
    eq: 'Jafnt og',
    gt: 'Stærra en',
    gte: 'Stærra eða jafnt',
    lt: 'Minna en',
    lte: 'Minna eða jafnt',
    on: 'Á',
    after: 'Eftir',
    onOrAfter: 'Á eða eftir',
    before: 'Fyrir',
    onOrBefore: 'Á eða fyrir',
    between: 'Milli dagsetninga',
    is: 'Er',
    isNot: 'Er ekki',
    isNull: 'Er tómt',
    isNotNull: 'Er ekki tómt',
  };

  const isValidIS = (s?: string) => !!s && dayjs(s, 'DD.MM.YYYY', true).isValid();
  const isValidISO = (s?: string) => !!s && dayjs(s, 'YYYY-MM-DD', true).isValid();

  const fmtChipDate = (s?: string) => {
    if (!s) return '—';
    if (isValidIS(s)) return dayjs(s, 'DD.MM.YYYY', true).format('DD.MM.YYYY');
    if (isValidISO(s)) return dayjs(s, 'YYYY-MM-DD', true).format('DD.MM.YYYY');
    const d = dayjs(s);
    return d.isValid() ? d.format('DD.MM.YYYY') : '—';
  };

  const filterColumns: GridColDef[] = useMemo(() => [
    { field: 'id', headerName: 'ID', type: 'number' },
    { field: 'shipRegistrationNumber', headerName: 'Skip númer' },
    { field: 'shipName', headerName: 'Skip' },
    { field: 'landingStarts', headerName: 'Löndun hefst', type: 'date' },
    { field: 'landingClosedAt', headerName: 'Löndun lokið', type: 'date' },
    { field: 'harbourName', headerName: 'Höfn' },
    { field: 'statusName', headerName: 'Staða' },
  ], []);

  const selectedLandings = useMemo(
    () => landings.filter((landing) => selectedLandingIds.includes(landing.id)),
    [landings, selectedLandingIds]
  );
  const landingStatusById = useMemo(
    () => new Map(selectedLandings.map((landing) => [landing.id, landing.landingStatus?.id])),
    [selectedLandings]
  );

  const landingColumns: GridColDef[] = useMemo(() => [
    { field: 'id', headerName: 'ID', minWidth: 70, flex: 0.3 },
    {
      field: 'landingStarts',
      headerName: 'Löndun hefst',
      minWidth: 160,
      flex: 0.9,
      valueGetter: (params) => params.row.landingStarts,
      valueFormatter: (params) => params.value ? dayjs(params.value).format('DD.MM.YYYY') : '',
    },
    {
      field: 'closedAt',
      headerName: 'Löndun lokið',
      minWidth: 160,
      flex: 0.9,
      valueGetter: (params) => params.row.closedAt,
      valueFormatter: (params) => params.value ? dayjs(params.value).format('DD.MM.YYYY') : '',
    },
    {
      field: 'shipNumber',
      headerName: 'Skip númer',
      minWidth: 130,
      flex: 0.7,
      valueGetter: (p) => p.row.ship?.shipRegistrationNumber,
    },
    {
      field: 'shipName',
      headerName: 'Skip',
      minWidth: 160,
      flex: 0.9,
      valueGetter: (p) => p.row.ship?.name,
    },
    {
      field: 'harbourName',
      headerName: 'Höfn',
      minWidth: 150,
      flex: 0.8,
      valueGetter: (p) => p.row.harbour?.harbourName,
    },
  ], []);


  useEffect(() => {
    if (props.user.role.id !== ADMIN_ROLE_ID) {
      return;
    }
    let cancelled = false;
    const timeout = setTimeout(() => {
      const fetchData = async () => {
        setLandingLoading(true);
        try {
          const data = await getLandings(undefined, true, 1, search, undefined, filterModel);
          if (cancelled) return;
          let filtered = (data || []).filter((landing) => landing.landingStatus?.id !== LandingStatuses.NEEDS_REWEIGHTING);

          const isDefaultLoad = search.trim() === '' && filterModel.clauses.length === 0;
          if (
            isDefaultLoad &&
            filtered.length === 0 &&
            (data?.length ?? 0) >= API_PAGINATION_SIZE
          ) {
            for (let page = 2; page <= MAX_LANDING_PAGES_TO_SCAN && filtered.length < API_PAGINATION_SIZE; page++) {
              if (cancelled) return;
              const nextPage = await getLandings(undefined, true, page, search, undefined, filterModel);
              if (cancelled) return;
              const nextPageFiltered = (nextPage || []).filter(
                (landing) => landing.landingStatus?.id !== LandingStatuses.NEEDS_REWEIGHTING
              );
              filtered = [...filtered, ...nextPageFiltered];

              if ((nextPage?.length ?? 0) < API_PAGINATION_SIZE) {
                break;
              }
            }
          }

          filtered = filtered.slice(0, API_PAGINATION_SIZE);
          setLandings(filtered);
          setSelectedLandingIds((prev) => {
            const ids = new Set(filtered.map((landing) => landing.id));
            const next = prev.filter((id) => ids.has(id));
            if (next.length === 0 && filtered.length) {
              return [filtered[0].id];
            }
            return next;
          });
        } finally {
          if (!cancelled) setLandingLoading(false);
        }
      };
      fetchData();
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [props.user, search, filterModel]);

  const fetchWeightNotesForSelectedLandings = useCallback(async () => {
    if (selectedLandingIds.length === 0) {
      setWeightNotes([]);
      setDetailLoading(false);
      return;
    }
    setDetailLoading(true);
    try {
      const results = await Promise.all(
        selectedLandingIds.map((id) => getWeightNotes(id, true))
      );
      const byId = new Map<number, WeightNote>();
      results.forEach((list) => {
        (list ?? []).forEach((note) => {
          if (note?.id != null) {
            byId.set(note.id, note);
          }
        });
      });
      setWeightNotes(Array.from(byId.values()));
    } finally {
      setDetailLoading(false);
    }
  }, [selectedLandingIds]);

  useEffect(() => {
    fetchWeightNotesForSelectedLandings();
    setSelectedWeightNoteIds([]);
    setSelectedWeightIds([]);
    setExpanded([]);
    setDetailSearch('');
  }, [fetchWeightNotesForSelectedLandings]);

  const hasWeightsSelected = selectedWeightIds.length > 0;
  const hasNotesSelected = selectedWeightNoteIds.length > 0;
  const effectiveTarget = editTarget ?? (hasWeightsSelected ? 'weights' : 'weightNotes');

  useEffect(() => {
    if (editTarget === 'weights' && !hasWeightsSelected) {
      setEditTarget(null);
    }
    if (editTarget === 'weightNotes' && !hasNotesSelected) {
      setEditTarget(null);
    }
  }, [editTarget, hasWeightsSelected, hasNotesSelected]);

  const selectedNotes = useMemo(
    () => weightNotes.filter((note) => selectedWeightNoteIds.includes(note.id)),
    [weightNotes, selectedWeightNoteIds]
  );

  const selectedWeights = useMemo(() => {
    if (selectedWeightIds.length === 0) return [];
    const ids = new Set(selectedWeightIds);
    return weightNotes
      .flatMap((note) => note.weights ?? [])
      .filter((weight) => ids.has(weight.id));
  }, [weightNotes, selectedWeightIds]);

  const getWeightNoteFieldValue = (note: WeightNote) => {
    switch (field) {
      case 'gearId':
        return note.gear?.id ?? '';
      case 'weightNoteTypeId':
        return note.weightNoteType?.id ?? '';
      case 'comment':
        return note.comment ?? '';
      default:
        return '';
    }
  };

  const getWeightFieldValue = (weight: CatchRegistration) => {
    switch (field) {
      case 'destinyId':
        return weight.destiny?.id ?? '';
      case 'fishingAreaId':
        return weight.fishingArea?.id ?? '';
      case 'storageMethodId':
        return weight.storageMethod?.id ?? '';
      case 'fishStockId':
        return weight.fishingStock?.id ?? '';
      case 'conditionId':
        return weight.condition?.id ?? '';
      case 'underKilogram':
        return weight.underKilogram ?? false;
      case 'icePercentage':
        return weight.icePercentage ?? '';
      case 'userComment':
        return weight.userComment ?? '';
      default:
        return '';
    }
  };

  
  const renderChangePreview = () => {
    const items = effectiveTarget === 'weights' ? selectedWeights : selectedNotes;
    if (items.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Engir hlutir valdir.
        </Typography>
      );
    }

    const totalAmount = effectiveTarget === 'weights'
      ? selectedWeights.reduce((sum, weight) => sum + (weight.quantity ?? 0), 0)
      : selectedNotes.flatMap((note) => note.weights ?? []).reduce((sum, weight) => sum + (weight.quantity ?? 0), 0);

    const resolveLandingIdForWeight = (weightId: number) => {
      const note = weightNotes.find((n) => (n.weights ?? []).some((w) => w.id === weightId));
      return note?.landingId;
    };

    const groups = new Map<number, { landing?: Landing; notes: WeightNote[]; weights: CatchRegistration[] }>();

    items.forEach((item) => {
      const landingId = effectiveTarget === 'weights'
        ? resolveLandingIdForWeight((item as CatchRegistration).id)
        : (item as WeightNote).landingId;
      if (!landingId) return;
      const landing = selectedLandings.find((l) => l.id === landingId);
      const group = groups.get(landingId) ?? { landing, notes: [], weights: [] };
      if (effectiveTarget === 'weights') {
        const note = weightNotes.find((n) => (n.weights ?? []).some((w) => w.id === (item as CatchRegistration).id));
        if (note && !group.notes.some((n) => n.id == note.id)) {
          group.notes.push(note);
        }
        group.weights.push(item as CatchRegistration);
      } else {
        group.notes.push(item as WeightNote);
      }
      groups.set(landingId, group);
    });

    const weightsForNote = (note: WeightNote) => {
      const noteWeights = note.weights ?? [];
      if (effectiveTarget === 'weights') {
        const selectedSet = new Set(selectedWeightIds);
        return noteWeights.filter((weight) => selectedSet.has(weight.id));
      }
      return noteWeights;
    };

    const fishTypesForWeights = (weights: CatchRegistration[]) =>
      Array.from(new Set(weights.map((weight) => weight.fishingStock?.name).filter(Boolean))) as string[];

    return (
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Chip size="small" label={effectiveTarget === 'weights' ? 'Vigtanir' : 'Vigtarnótur'} />
          <Chip size="small" variant="outlined" label={`${items.length} valdir`} />
          <Chip size="small" color="primary" label={activeField?.label ?? '-'} />
          <Chip size="small" color="success" label={`Nýtt gildi: ${resolveLabel(toValue) || 'Óútfyllt'}`} />
          <Chip size="small" variant="outlined" label={`Samtals magn: ${new Intl.NumberFormat('is-IS', { maximumFractionDigits: 2 }).format(totalAmount)} kg`} />
        </Stack>

        {Array.from(groups.entries()).map(([landingId, group], idx) => (
          <Paper
            key={landingId}
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              borderLeft: '6px solid',
              borderLeftColor: (theme) => idx % 2 === 0 ? theme.palette.primary.main : theme.palette.secondary.main,
              bgcolor: 'background.paper',
              boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)'
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Chip size="small" label={`Löndun #${landingId}`} />
              {group.landing?.ship?.name && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <DirectionsBoatIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    {`${group.landing.ship?.name} ${group.landing.ship?.shipRegistrationNumber ?? ''}`.trim()}
                  </Typography>
                </Stack>
              )}
              {group.landing?.landingStarts && (
                <Typography variant="body2" color="text.secondary">
                  {dayjs(group.landing.landingStarts).format('DD.MM.YYYY')}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              <Chip size="small" color="info" label={`Vigtarnótur: ${group.notes.length}`} />
              <Chip size="small" variant="outlined" label={`Vigtanir: ${group.weights.length || group.notes.reduce((sum, n) => sum + (n.weights?.length ?? 0), 0)}`} />
            </Stack>

            <Stack spacing={1.5} sx={{ pl: { xs: 0, md: 0.5 } }}>
              {group.notes.map((note) => {
                const noteWeights = weightsForNote(note);
                const noteFishTypes = fishTypesForWeights(noteWeights);
                const noteAmount = noteWeights.reduce((sum, weight) => sum + (weight.quantity ?? 0), 0);

                return (
                  <Paper key={note.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }}>
                      <Typography fontWeight={700}>Vigtarnóta #{note.noteNumber}</Typography>
                      {note.weightNoteType?.name && <Chip size="small" variant="outlined" label={note.weightNoteType.name} />}
                      {note.gear?.name && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <PhishingIcon fontSize="small" color="action" />
                          <Typography variant="body2">{note.gear.name}</Typography>
                        </Stack>
                      )}
                      <Box sx={{ flex: 1 }} />
                      <Chip size="small" color="info" label={`Magn: ${new Intl.NumberFormat('is-IS', { maximumFractionDigits: 2 }).format(noteAmount)} kg`} />
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mt: 1 }}>
                      {note.buyer?.name && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography variant="body2">{note.buyer.name}</Typography>
                        </Stack>
                      )}
                      {note.receiver?.name && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <GroupsIcon fontSize="small" color="action" />
                          <Typography variant="body2">{note.receiver.name}</Typography>
                        </Stack>
                      )}
                    </Stack>

                    {noteFishTypes.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                        {noteFishTypes.map((name) => (
                          <Box
                            key={`${note.id}-${name}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1,
                              py: 0.25,
                              borderRadius: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'background.paper',
                            }}
                          >
                            <SetMealIcon fontSize="small" color="primary" />
                            <Typography variant="body2">{name}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {effectiveTarget === 'weightNotes' && (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">N?verandi</Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>{resolveLabel(getWeightNoteFieldValue(note)) || '-'}</Typography>
                        <CompareArrowsIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">Nýtt</Typography>
                        <Typography variant="body2" fontWeight={700} color="success.main">{resolveLabel(toValue) || 'Ófyllt'}</Typography>
                      </Stack>
                    )}

                    <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                      {noteWeights.map((weight) => (
                        <Paper
                          key={weight.id}
                          variant="outlined"
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Chip size="small" label={`Vigtun #${weight.id}`} />
                          {weight.fishingStock?.name && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <SetMealIcon fontSize="small" color="primary" />
                              <Typography variant="body2">{weight.fishingStock.name}</Typography>
                            </Stack>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            Magn: {new Intl.NumberFormat('is-IS', { maximumFractionDigits: 2 }).format(weight.quantity ?? 0)} kg
                          </Typography>
                          {weight.underKilogram && <Chip size="small" color="warning" label="Undirmál" />}
                          {weight.storageMethod?.name && <Chip size="small" variant="outlined" label={weight.storageMethod.name} />}
                          {effectiveTarget === 'weights' && (
                            <>
                              <CompareArrowsIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>{resolveLabel(getWeightFieldValue(weight)) || '-'}</Typography>
                              <Typography variant="body2" fontWeight={700} color="success.main">{resolveLabel(toValue) || ''}</Typography>
                            </>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  };

  const validateCatchCombinations = () => {
    if (effectiveTarget !== 'weights') return { ok: true as const };
    const comboFields = new Set(['destinyId', 'fishingAreaId', 'storageMethodId', 'fishStockId', 'conditionId', 'underKilogram']);
    if (!comboFields.has(field)) return { ok: true as const };
    if (catchCombinations.length === 0) {
      return { ok: false as const, message: 'Engin catchCombination gögn fundust. Get ekki staðfest samsetningar.' };
    }

    const invalidIds: number[] = [];
    selectedWeights.forEach((weight) => {
      const nextFishingAreaId = field === 'fishingAreaId' ? Number(toValue) : weight.fishingArea?.id;
      const nextStorageMethodId = field === 'storageMethodId' ? Number(toValue) : weight.storageMethod?.id;
      const nextDestinyId = field === 'destinyId' ? Number(toValue) : weight.destiny?.id;
      const nextFishStockId = field === 'fishStockId' ? Number(toValue) : weight.fishingStock?.id;
      const nextConditionId = field === 'conditionId' ? Number(toValue) : weight.condition?.id;
      const nextUnderKilogram = field === 'underKilogram'
        ? (toValue === true || toValue === 'true')
        : !!weight.underKilogram;

      if (
        !nextFishingAreaId ||
        !nextStorageMethodId ||
        !nextDestinyId ||
        !nextFishStockId ||
        !nextConditionId
      ) {
        invalidIds.push(weight.id);
        return;
      }

      const isAllowed = catchCombinations.some((combination) =>
        combination.fishingAreaId === nextFishingAreaId &&
        combination.storageMethodId === nextStorageMethodId &&
        combination.destinyId === nextDestinyId &&
        combination.fishStockId === nextFishStockId &&
        combination.conditionId === nextConditionId &&
        combination.isUnderKilogram === nextUnderKilogram
      );

      if (!isAllowed) {
        invalidIds.push(weight.id);
      }
    });

    if (invalidIds.length > 0) {
      return {
        ok: false as const,
        message: `Ólögleg samsetning fyrir ${invalidIds.length} vigtun${invalidIds.length > 1 ? 'ir' : ''}.`
      };
    }

    return { ok: true as const };
  };

  useEffect(() => {
    const nextField = effectiveTarget === 'weightNotes' ? 'gearId' : 'destinyId';
    setField(nextField);
    setToValue('');
  }, [effectiveTarget]);

  const fieldOptions: FieldOption[] = useMemo(() => {
    if (effectiveTarget === 'weightNotes') {
      return [
        { value: 'gearId', label: 'Veiðarfæri', type: 'select', options: gears },
        { value: 'weightNoteTypeId', label: 'Tegund vigtarnótu', type: 'select', options: weightNoteTypes },
        { value: 'comment', label: 'Athugasemd', type: 'text' },
      ];
    }
    return [
      { value: 'destinyId', label: 'Afdrif', type: 'select', options: destinies },
      { value: 'fishingAreaId', label: 'Veiðisvæði', type: 'select', options: fishingAreas },
      { value: 'storageMethodId', label: 'Geymsluaðferð', type: 'select', options: storageMethods },
      { value: 'fishStockId', label: 'Veiðistofn', type: 'select', options: fishingStocks },
      { value: 'conditionId', label: 'Ástand', type: 'select', options: conditions },
      { value: 'underKilogram', label: 'Undirmál', type: 'boolean' },
      { value: 'icePercentage', label: 'Ís (%)', type: 'number' },
      { value: 'userComment', label: 'Athugasemd', type: 'text' },
    ];
  }, [effectiveTarget, gears, weightNoteTypes, destinies, fishingAreas, storageMethods, fishingStocks, conditions]);

  const activeField = fieldOptions.find((opt) => opt.value === field) ?? fieldOptions[0];

  const selectedIds = effectiveTarget === 'weightNotes' ? selectedWeightNoteIds : selectedWeightIds;
  const canApply = selectedIds.length > 0 &&
    toValue !== '' &&
    toValue !== undefined &&
    toValue !== null &&
    bulkComment.trim().length > 0;

  const updateExpanded = (id: number) => {
    setExpanded((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleWeightNoteSelection = (note: WeightNote) => {
    const id = note.id;
    const weightIds = (note.weights ?? []).map((w: CatchRegistration) => w.id);
    const noteSelected = selectedWeightNoteIds.includes(id);
    setSelectedWeightNoteIds((prev) => noteSelected ? prev.filter((x) => x !== id) : [...prev, id]);
    setSelectedWeightIds((prev) => {
      if (noteSelected) {
        return prev.filter((weightId) => !weightIds.includes(weightId));
      }
      return weightIds.length === 0 ? prev : Array.from(new Set([...prev, ...weightIds]));
    });
  };

  const toggleWeightSelection = (id: number) => {
    setSelectedWeightIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const optionLabel = (option?: { id: number; name: string }) => option?.name ?? '';

  const resolveLabel = (value: string | number | boolean | '') => {
    if (value === '' || value === null || value === undefined) return '';
    if (activeField?.type === 'select') {
      const option = activeField.options?.find((opt: { id: number; name: string }) => opt.id === Number(value));
      return optionLabel(option);
    }
    if (activeField?.type === 'boolean') {
      return value === true || value === 'true' ? 'Já' : 'Nei';
    }
    return String(value);
  };

  const matchesSearch = (value: string, searchValue: string) =>
    value.toLowerCase().includes(searchValue.toLowerCase());

  const isValidWeightNote = useCallback((note: WeightNote) => {
    const landingStatusId = landingStatusById.get(note.landingId);
    if (!landingStatusId) return false;
    if (!note.isClosed) return false;
    if (landingStatusId === LandingStatuses.OPEN) {
      return (
        note.isCredit === false &&
        note.undoWeightNoteId === null &&
        note.weightNoteType.id !== WeightNoteTypes.FINAL_REWEIGHTING &&
        !(
          note.weightNoteType.id === WeightNoteTypes.PRODUCT_SAMPLE_ID &&
          note.isClosed &&
          note.referenceWeightNoteId !== null
        )
      );
    }
    return (
      note.isCredit === false &&
      note.undoWeightNoteId === null &&
      note.weightNoteType.id !== WeightNoteTypes.FINAL_REWEIGHTING &&
      !(note.isClosed && note.weightNoteType.id === WeightNoteTypes.TO_REWEIGHING) &&
      !(note.isClosed && note.weightNoteType.id === WeightNoteTypes.TO_HOME_REWEIGHING) &&
      !(
        note.weightNoteType.id === WeightNoteTypes.PRODUCT_SAMPLE_ID &&
        note.isClosed &&
        note.referenceWeightNoteId !== null
      )
    );
  }, [landingStatusById]);

  const validWeightNotes = useMemo(
    () => weightNotes.filter(isValidWeightNote),
    [weightNotes, isValidWeightNote]
  );

  useEffect(() => {
    const validIds = new Set(validWeightNotes.map((note) => note.id));
    setSelectedWeightNoteIds((prev) => prev.filter((id) => validIds.has(id)));
  }, [validWeightNotes]);

  const filteredWeightNotes = useMemo(() => {
    if (!detailSearch.trim()) return validWeightNotes;
    const query = detailSearch.trim();
    return validWeightNotes.filter((note) => {
      const noteMatch = [
        note.noteNumber?.toString() ?? '',
        note.weightNoteType?.name ?? '',
        note.gear?.name ?? '',
        note.buyer?.name ?? '',
        note.receiver?.name ?? '',
      ].some((value) => matchesSearch(value, query));

      const weightsMatch = (note.weights ?? []).some((weight: CatchRegistration) => [
        weight.id?.toString() ?? '',
        weight.fishingStock?.name ?? '',
        weight.condition?.name ?? '',
        weight.destiny?.name ?? '',
      ].some((value) => matchesSearch(value, query)));

      return noteMatch || weightsMatch;
    });
  }, [detailSearch, validWeightNotes]);

  const openConfirmDialog = () => {
    setBulkSubmitError('');
    setBulkRefreshFailed(false);
    setConfirmOpen(true);
  };

  const resetBulkEditAfterSubmit = () => {
    setSelectedLandingIds([]);
    setSelectedWeightNoteIds([]);
    setSelectedWeightIds([]);
    setEditTarget(null);
    setToValue('');
    setBulkComment('');
    setBulkSubmitError('');
    setBulkRefreshFailed(false);
    setExpanded([]);
    setDetailSearch('');
    setActiveStep(0);
  };

  const closeConfirmDialog = () => {
    if (isSubmitting) {
      return;
    }
    if (bulkRefreshFailed) {
      resetBulkEditAfterSubmit();
    } else {
      setBulkSubmitError('');
      setBulkRefreshFailed(false);
    }
    setConfirmOpen(false);
  };

  const retryBulkRefresh = async () => {
    if (isSubmitting) {
      return;
    }
    setBulkSubmitError('');
    setIsSubmitting(true);
    try {
      await props.refetchLandings();
      await fetchWeightNotesForSelectedLandings();
      resetBulkEditAfterSubmit();
      setConfirmOpen(false);
      showSnackbar('Gögn uppfærð.', 'success');
    } catch (error) {
      setBulkRefreshFailed(true);
      setBulkSubmitError('Ekki tókst að sækja uppfærð gögn. Reyndu aftur.');
      showSnackbar('Uppfærsla gagna mistókst.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!canApply || isSubmitting) {
      return;
    }
    const validation = validateCatchCombinations();
    if (!validation.ok) {
      setBulkSubmitError(validation.message);
      showSnackbar(validation.message, 'error');
      return;
    }
    setBulkSubmitError('');
    setBulkRefreshFailed(false);
    setIsSubmitting(true);
    try {
      await postBulkCorrection({
        target: effectiveTarget,
        ids: selectedIds,
        field,
        fromValue: undefined,
        toValue,
        comment: bulkComment.trim(),
      });
    } catch (error) {
      setBulkSubmitError('Villa kom upp við að senda leiðréttingu. Reyndu aftur.');
      showSnackbar('Villa kom upp við að senda leiðréttingu.', 'error');
      setIsSubmitting(false);
      return;
    }

    try {
      await props.refetchLandings();
      await fetchWeightNotesForSelectedLandings();
      resetBulkEditAfterSubmit();
      setConfirmOpen(false);
      showSnackbar('Leiðrétting skráð og gögn uppfærð.', 'success');
    } catch (error) {
      setBulkRefreshFailed(true);
      setBulkSubmitError('Leiðrétting var skráð, en ekki tókst að sækja uppfærð gögn.');
      showSnackbar('Leiðrétting skráð, en uppfærsla gagna mistókst.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (props.user.role.id !== ADMIN_ROLE_ID) {
    return (
      <Box sx={{ width: '100%', alignSelf: 'stretch' }}>
        <Box sx={{ p: 4 }}>
          <Typography variant="h6">Aðgangur hafnaður.</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: props.embedded ? '100%' : '100vh',
        height: props.embedded ? '100%' : 'auto',
        bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#f4f6f8',
        width: '100%',
        alignSelf: 'stretch',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: { xs: 1.5, md: 2 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            mb: 2,
            p: { xs: 1.5, md: 2 },
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))'
              : 'linear-gradient(135deg, rgba(25,118,210,0.14), rgba(255,255,255,0.96))',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Stack spacing={0.25}>
              <Typography variant="h5" fontWeight={800}>Leiðréttingar</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {props.embedded && props.onClose && (
                <IconButton onClick={props.onClose} aria-label="Loka">
                  <CloseIcon />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Box>

        <Stepper activeStep={activeStep} alternativeLabel>
          <Step>
            <StepLabel>Veldu landanir</StepLabel>
          </Step>
          <Step>
            <StepLabel>Veldu vigtarnótur eða vigtanir</StepLabel>
          </Step>
          <Step>
            <StepLabel>Stilla leiðréttingu</StepLabel>
          </Step>
        </Stepper>

        <Box sx={{ mt: 2, flex: 1, minHeight: 0 }}>
          {activeStep === 0 && (
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>1</Box>
                  <Stack spacing={0.5}>
                    <Typography variant="h6" fontWeight={700}>Veldu landanir</Typography>
                  </Stack>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <TextField
                    label="Leit að löndunum"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                    fullWidth
                  />
                  <Button variant="outlined" onClick={() => setFiltersOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                    Síur
                  </Button>
                </Stack>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {(filterModel.clauses ?? []).map((c, idx) => {
                    const col = filterColumns.find((fc) => fc.field === c.field);
                    const header = col?.headerName || c.field;
                    const op = OP_ISL[c.operator ?? ''] ?? c.operator ?? '';

                    let v = '';
                    if (c.type === 'date') {
                      if (c.operator === 'between' && typeof c.value === 'object' && c.value) {
                        const vv = c.value as { start?: string; end?: string };
                        v = `${fmtChipDate(vv.start)} → ${fmtChipDate(vv.end)}`;
                      } else if (typeof c.value === 'string') {
                        v = fmtChipDate(c.value);
                      }
                    } else if (Array.isArray(c.value)) {
                      v = c.value.join(' eða ');
                    } else if (c.value !== undefined && c.value !== null) {
                      v = String(c.value);
                    }

                    const label = `${header} ${op}${v ? ` ${v}` : ''}`;

                    return (
                      <Chip
                        key={`${c.field}-${idx}`}
                        label={label}
                        onClick={() => setFiltersOpen(true)}
                        onDelete={() => {
                          const next = {
                            ...filterModel,
                            clauses: filterModel.clauses.filter((_, i) => i !== idx),
                          };
                          setFilterModel(next);
                        }}
                        variant="outlined"
                        sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      />
                    );
                  })}
                </Box>

                <Paper variant='outlined' sx={{ borderRadius: 2, flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  <DataGrid
                    rows={landings}
                    columns={landingColumns}
                    loading={landingLoading}
                    autoHeight={false}
                    disableRowSelectionOnClick
                    hideFooterPagination
                    checkboxSelection
                    rowSelectionModel={selectedLandingIds}
                    onRowSelectionModelChange={(model: GridRowSelectionModel) => setSelectedLandingIds(model as number[])}
                    sx={{
                      height: '100%',
                      '& .MuiDataGrid-columnHeaders': { bgcolor: 'background.default' },
                      '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
                      '& .MuiDataGrid-row.Mui-selected': { bgcolor: 'action.selected' },
                    }}
                  />
                </Paper>

                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button disabled>Til baka</Button>
                  <Button variant="contained" onClick={() => setActiveStep(1)} disabled={selectedLandingIds.length === 0}>Næsta</Button>
                </Stack>
              </Stack>
            </Paper>
          )}

          {activeStep === 1 && (
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'secondary.main', color: 'secondary.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>2</Box>
                  <Stack spacing={0.5}>
                    <Typography variant="h6" fontWeight={700}>Veldu vigtarnótur eða vigtanir</Typography>
                  </Stack>
                </Stack>

                <Stack spacing={1.5}>
                  <TextField
                    label="Leit í vigtarnótum og vigtunum"
                    value={detailSearch}
                    onChange={(event) => setDetailSearch(event.target.value)}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                    size="small"
                    fullWidth
                  />
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      const noteIds = filteredWeightNotes.map((note) => note.id);
                      const allSelected = noteIds.length > 0 && noteIds.every((id) => selectedWeightNoteIds.includes(id));
                      if (allSelected) {
                        setSelectedWeightNoteIds((prev) => prev.filter((id) => !noteIds.includes(id)));
                        const weightIds = filteredWeightNotes.flatMap((note) => note.weights ?? []).map((weight) => weight.id);
                        setSelectedWeightIds((prev) => prev.filter((id) => !weightIds.includes(id)));
                      } else {
                        setSelectedWeightNoteIds((prev) => Array.from(new Set([...prev, ...noteIds])));
                        const weightIds = filteredWeightNotes.flatMap((note) => note.weights ?? []).map((weight) => weight.id);
                        setSelectedWeightIds((prev) => Array.from(new Set([...prev, ...weightIds])));
                      }
                    }}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Velja allar vigtarnótur í lista
                  </Button>
                </Stack>

                <Divider />

                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 1 }}>
                  {detailLoading && (
                    <Typography color="text.secondary">Hleð inn gögnum...</Typography>
                  )}

                  {!detailLoading && selectedLandingIds.length === 0 && (
                    <Typography color="text.secondary">Engin löndun valin.</Typography>
                  )}

                  {!detailLoading && selectedLandingIds.length > 0 && filteredWeightNotes.length === 0 && (
                    <Typography color="text.secondary">Engar vigtarnótur fundust.</Typography>
                  )}

                  {!detailLoading && selectedLandingIds.length > 0 && filteredWeightNotes.map((note, idx) => {
                    const isExpanded = expanded.includes(note.id);
                    const accent = idx % 2 === 0 ? 'primary.main' : 'secondary.main';
                    const weights = note.weights ?? [];
                    const visibleWeights = detailSearch.trim()
                      ? weights.filter((weight: CatchRegistration) => [
                        weight.id?.toString() ?? '',
                        weight.fishingStock?.name ?? '',
                        weight.condition?.name ?? '',
                        weight.destiny?.name ?? '',
                      ].some((value) => matchesSearch(value, detailSearch.trim())))
                      : weights;

                    return (
                      <Accordion
                        key={note.id}
                        expanded={isExpanded}
                        onChange={() => updateExpanded(note.id)}
                        sx={{
                          borderLeft: '4px solid',
                          borderColor: accent,
                          bgcolor: isExpanded ? 'action.hover' : 'background.paper',
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                            <Checkbox
                              checked={selectedWeightNoteIds.includes(note.id)}
                              onChange={() => toggleWeightNoteSelection(note)}
                              onClick={(event) => event.stopPropagation()}
                            />
                            <Stack spacing={0.2} sx={{ ml: 1 }}>
                              <Typography fontWeight={600}>Vigtarnóta #{note.noteNumber}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {note.weightNoteType?.name} · {note.gear?.name}
                              </Typography>
                            </Stack>
                            <Box sx={{ flex: 1 }} />
                            <Chip label={`${weights.length} Vigtanir`} size="small" />
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={1.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                              <Chip label={`Kaupandi: ${note.buyer?.name ?? '-'}`} size="small" />
                              <Chip label={`Viðtakandi: ${note.receiver?.name ?? '-'}`} size="small" />
                              <Chip label={`Skráð: ${fmtDateTime(note.weightNoteRegistered)}`} size="small" />
                            </Stack>
                            <Divider />
                            {visibleWeights.length === 0 && (
                              <Typography color="text.secondary">Engar vigtanir uppfylla leit.</Typography>
                            )}
                            {visibleWeights.map((weight: CatchRegistration) => (
                              <Paper
                                key={weight.id}
                                variant="outlined"
                                sx={{
                                  p: 1,
                                  borderRadius: 2,
                                  bgcolor: 'background.default',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Checkbox
                                  checked={selectedWeightIds.includes(weight.id)}
                                  onChange={() => toggleWeightSelection(weight.id)}
                                />
                                <Typography fontWeight={700}>Vigtun #{weight.id}</Typography>
                                <Chip size="small" color="primary" variant="outlined" label={weight.fishingStock?.name ?? 'Stofn vantar'} />
                                <Chip size="small" color="secondary" variant="outlined" label={weight.condition?.name ?? 'Ástand vantar'} />
                                <Chip size="small" color="info" variant="outlined" label={weight.destiny?.name ?? 'Afdrif vantar'} />
                                {weight.storageMethod?.name && (
                                  <Chip size="small" color="success" variant="outlined" label={weight.storageMethod.name} />
                                )}
                                {weight.underKilogram && (
                                  <Chip size="small" color="warning" label="Undirmál" />
                                )}
                                <Chip
                                  size="small"
                                  color="default"
                                  sx={{ fontWeight: 600 }}
                                  label={`${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(weight.quantity ?? 0)} kg`}
                                />
                              </Paper>
                            ))}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>

                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button onClick={() => setActiveStep(0)}>Til baka</Button>
                  <Button variant="contained" onClick={() => setActiveStep(2)} disabled={selectedWeightNoteIds.length === 0 && selectedWeightIds.length === 0}>Næsta</Button>
                </Stack>
              </Stack>
            </Paper>
          )}

          {activeStep === 2 && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'success.main', color: 'success.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>3</Box>
                <Stack spacing={0.5}>
                  <Typography variant="h6" fontWeight={700}>Stilla leiðréttingu</Typography>
                </Stack>
              </Stack>
              <Divider />
              <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Tabs
                    value={effectiveTarget === 'weights' ? 1 : 0}
                    onChange={(_, value) => setEditTarget(value === 1 ? 'weights' : 'weightNotes')}
                    sx={{
                      minHeight: 36,
                      '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 600 },
                    }}
                  >
                    <Tab label="Vigtarnótur" disabled={!hasNotesSelected} />
                    <Tab label="Vigtanir" disabled={!hasWeightsSelected} />
                  </Tabs>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch" sx={{ flexShrink: 0 }}>
                  <TextField
                    select
                    label="Breyta sviði"
                    value={field}
                    onChange={(event) => setField(event.target.value)}
                    fullWidth
                    sx={{ minWidth: 240 }}
                    SelectProps={{ MenuProps: { disablePortal: true, PaperProps: { sx: { maxHeight: 320 } } } }}
                  >
                    {fieldOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>

                  {activeField?.type === 'select' && (
                    <Autocomplete
                      options={activeField.options ?? []}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={(activeField.options ?? []).find((opt) => opt.id === Number(toValue)) ?? null}
                      onChange={(_, value) => setToValue(value ? value.id : '')}
                      fullWidth
                      disablePortal
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Setja í"
                          sx={{ minWidth: 240 }}
                        />
                      )}
                    />
                  )}

                  {activeField?.type === 'text' && (
                    <TextField
                      label="Setja í"
                      value={toValue}
                      onChange={(event) => setToValue(event.target.value)}
                      fullWidth
                      sx={{ minWidth: 240 }}
                    />
                  )}

                  {activeField?.type === 'number' && (
                    <TextField
                      label="Setja í"
                      type="number"
                      value={toValue}
                      onChange={(event) => setToValue(event.target.value === '' ? '' : Number(event.target.value))}
                      fullWidth
                      sx={{ minWidth: 240 }}
                    />
                  )}

                  {activeField?.type === 'boolean' && (
                    <TextField
                      select
                      label="Setja í"
                      value={toValue}
                      onChange={(event) => setToValue(event.target.value as string)}
                      fullWidth
                      sx={{ minWidth: 240 }}
                      SelectProps={{ MenuProps: { disablePortal: true, PaperProps: { sx: { maxHeight: 320 } } } }}
                    >
                      <MenuItem value="true">Já</MenuItem>
                      <MenuItem value="false">Nei</MenuItem>
                    </TextField>
                  )}
                  <TextField
                    label="Athugasemd"
                    value={bulkComment}
                    onChange={(event) => setBulkComment(event.target.value)}
                    required
                    fullWidth
                    minRows={2}
                    sx={{ minWidth: 240 }}
                  />
                </Stack>
              </Stack>

              <Paper
                variant='outlined'
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'background.default',
                  maxHeight: { xs: 220, md: 320 },
                  overflow: 'auto',
                  mt: 1,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>Forskoðun</Typography>
                {renderChangePreview()}
              </Paper>

              <Box
                sx={{
                  mt: 'auto',
                  pt: 1,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button onClick={() => setActiveStep(1)}>Til baka</Button>
                  <Button
                    variant="contained"
                    startIcon={<TaskAltIcon />}
                    onClick={openConfirmDialog}
                    disabled={!canApply || isSubmitting}
                  >
                    Staðfesta leiðréttingu
                  </Button>
                </Stack>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>
      <FiltersDialog
        open={filtersOpen}
        onClose={(applied) => {
          setFiltersOpen(false);
          if (JSON.stringify(applied) !== JSON.stringify(filterModel)) {
            setFilterModel(applied);
          }
        }}
        columns={filterColumns}
        value={filterModel}
      />
      <Dialog
        open={confirmOpen}
        onClose={closeConfirmDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
        disableEscapeKeyDown={isSubmitting}
      >
        <DialogTitle>Staðfesta leiðréttingu</DialogTitle>
        {isSubmitting && <LinearProgress />}
        <DialogContent dividers sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Stack spacing={2} sx={{ minHeight: 0 }}>
            {bulkSubmitError && (
              <Alert severity="error">
                {bulkSubmitError}
              </Alert>
            )}
            {isSubmitting && (
              <Alert severity="info" icon={<CircularProgress size={20} color="inherit" />}>
                {bulkRefreshFailed
                  ? 'Sæki uppfærð gögn. Bíddu þar til aðgerðinni lýkur.'
                  : 'Sendi leiðréttingu og sæki uppfærð gögn. Bíddu þar til aðgerðinni lýkur.'}
              </Alert>
            )}
            <Typography>
              Þú ert að breyta <strong>{selectedIds.length}</strong>{' '}
              {effectiveTarget === 'weights' ? 'vigtunum' : 'vigtarnótum'}.
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                maxHeight: '60vh',
                overflow: 'auto',
                opacity: isSubmitting ? 0.65 : 1,
                pointerEvents: isSubmitting ? 'none' : 'auto',
              }}
            >
              {renderChangePreview()}
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={isSubmitting}>Hætta við</Button>
          <Button
            variant="contained"
            onClick={bulkRefreshFailed ? retryBulkRefresh : handleConfirm}
            disabled={isSubmitting || (!bulkRefreshFailed && !canApply)}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {isSubmitting ? 'Uppfæri...' : bulkRefreshFailed ? 'Sækja gögn aftur' : 'Staðfesta'}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent
        successMsg={snackbarMsg}
        isOpen={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        autoHideDurationMS={3500}
        severity={snackbarSeverity}
      />
    </Box>
  );
};

export default AdminCorrections;
